import React, { useCallback, useEffect, useRef, useState } from "react";

import { A4_PAPER } from "../../../components/Print/engine/sheetLayout";

const MM_TO_CSS_PX = 96 / 25.4;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/** Display zoom is deliberately outside the print document and never mutates geometry. */
function SheetPreview({ html, title, paper = A4_PAPER, tt, onSelectPosition }) {
  const viewportRef = useRef(null);
  const iframeRef = useRef(null);
  const onSelectRef = useRef(onSelectPosition);
  onSelectRef.current = onSelectPosition;
  const pageWidthPx = paper.widthMM * MM_TO_CSS_PX;
  const pageHeightPx = paper.heightMM * MM_TO_CSS_PX;
  const [fitScale, setFitScale] = useState(0.5);
  const [zoom, setZoom] = useState(null);
  const [documentHeight, setDocumentHeight] = useState(pageHeightPx);
  const scale = zoom ?? fitScale;

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const measure = () => {
      const width = Math.max(1, viewport.clientWidth - 24);
      setFitScale(clamp(width / pageWidthPx, 0.2, 1.25));
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [pageWidthPx]);

  const handleLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    setDocumentHeight(Math.max(pageHeightPx, doc.documentElement.scrollHeight));
    doc.addEventListener("click", (event) => {
      const slot = event.target?.closest?.("[data-select-position]");
      if (!slot) return;
      const position = Number(slot.getAttribute("data-select-position"));
      if (Number.isInteger(position)) onSelectRef.current?.(position);
    });
  }, [pageHeightPx]);

  const changeZoom = (delta) => setZoom(clamp((zoom ?? fitScale) + delta, 0.2, 1.5));
  const label = tt ? tt("previewZoom") : "Preview zoom";

  return (
    <div className="pc-sheet-preview-wrap">
      <div className="pc-sheet-preview-tools" role="group" aria-label={label}>
        <button type="button" onClick={() => changeZoom(-0.1)} aria-label={tt ? tt("previewZoomOut") : "Zoom out"}>−</button>
        <strong>{Math.round(scale * 100)}%</strong>
        <button type="button" onClick={() => changeZoom(0.1)} aria-label={tt ? tt("previewZoomIn") : "Zoom in"}>+</button>
        <button type="button" onClick={() => setZoom(null)}>{tt ? tt("previewFit") : "Fit"}</button>
      </div>
      <div ref={viewportRef} className="pc-sheet-preview">
        <div className="pc-sheet-preview__canvas" style={{ width: pageWidthPx * scale, height: documentHeight * scale }}>
          <iframe
            ref={iframeRef}
            title={title}
            srcDoc={html}
            onLoad={handleLoad}
            tabIndex={-1}
            className="pc-sheet-preview__frame"
            style={{ width: pageWidthPx, height: documentHeight, transform: `scale(${scale})` }}
          />
        </div>
      </div>
    </div>
  );
}

export default SheetPreview;
